import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import Iframe from 'react-iframe';
import Clipboard from 'react-clipboard.js';
import EmailIcon from './assets/email.png'

import List from './list';
import { Speaker } from './entity/type';
import {
  ColContainer,
  RowContainer,
  Text,
  DText,
  LinkImg,
  Logo,
} from './components/atoms';

const Header = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 4px 20px;
  cursor: pointer;
`

const MIframeContainer = styled.div`
  position: relative;
  width: 100vw;

  padding-top: 52.65%;

  margin-bottom: 36px;

  @media (min-width: 885px) {
    width: 885px;
    padding-top: 469.95px;
  }
`;

const MIframe = styled(Iframe)`
  width: 100%;
  height: 100%;

  position: absolute;
  top: 0;
  left: 0;
  background-color: grey;
`;

const TextContainer = styled(ColContainer)`
  width: 100%;
  max-width: 1024px;

  padding: 0px 20px;
`;

const SectionNameContainer = styled.div`
  position: relative;

  align-self: flex-start;

  width: auto;
  height: 24px;

  padding: 0 4px;
  background: linear-gradient(
    to bottom,
    transparent 50%,
    ${(props: { bg?: string }) => (props.bg ? `${props.bg} 50%` : 'black')}
  );
`;

const DLine = styled.hr`
  width: 100%;
  height: 1px;
  margin: 0px 0px 16px 0px;
  color: rgba(255, 255, 255, 0.2);
  background-color: rgba(255, 255, 255, 0.2);
  border: 0px;
`;

const ShareBtn = styled(Clipboard)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background-color: ${(props: { bg: string }) => props.bg};

  border-radius: 12px;

  cursor: pointer;

  @media (min-width: 800px) {
    padding: 8px 12px;
  }

  &:hover {
    opacity: 80%;
  }
`;

const ShareText = styled(Text)`
  color: white;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;

  @media (min-width: 800px) {
    font-weight: 800;
  }
`;

const SpeakersWrapper = styled.div`
  width: 100%;
  padding: 8px 0 16px;
`

const ProfileWrapper = styled(RowContainer)`
  width: 100%;
  align-items: center;
  padding-top: 16px;
`

const ProfileImage = styled.img`
  width: 48px;
  height: 48px;
  
  margin-right: 8px;
  border-radius: 50%;
`

const Email = styled.a`
  display: flex;
  align-items: center;
  margin-left: auto;
  height: 40px;
  padding: 0 12px;
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`

const ExternalWrapper = styled.div`
  width: 100%;
  padding: 8px 0 16px;
`

const External = styled.a`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;

  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`

const ExternalImage = styled.img`
  width: 36px;
  height: 36px;
  overflow: hidden;
`

function Detail({
  match: {
    params: { index },
  },
}: RouteComponentProps<{ index: string }>) {
  const history = useHistory();

  const temp = index.split('-');
  const [scIndex, ssIndex] = useMemo(() => temp.map(number => parseInt(number)), [temp])

  const section = List[scIndex]
  const session = section?.sessions?.[ssIndex]

  const goToHome = useCallback(() => {
    history.replace('/2021');
  }, [history])

  if (!section || !session) {
    goToHome()
  }

  const scName = section?.name
  const scColor = section?.color
  
  useEffect(function setOgTag() {
    document?.querySelector('meta[property="og:title"]')?.setAttribute("content", session.title);
    document?.querySelector('meta[property="og:image"]')?.setAttribute("content", session.thumbnailImage);
  }, [session.thumbnailImage, session.title])

  const canUseShare = !!navigator.share
  const [shareUrl, setShareUrl] = useState('')
  const shareText = `YOURSSU의 Road to Pro 2021 컨퍼런스\n"${session.title}" 보러가기`
  const clipboard = `${shareText}\n${shareUrl}`

  useEffect(function setShareUrlAsynchronously() {
    setShareUrl(window.location.href)
  }, [])

  const handleClickShare = () => {
    if (canUseShare) {
      const shareData = {
        title: 'YORSSU Road to Pro',
        text: clipboard,
      }
      navigator.share(shareData)
    } else {
      window.alert('링크 복사됨')
    }
  }

  const ProfileComponent = useCallback((speaker: Speaker) => (
    <ProfileWrapper key={speaker.name}>
      <ProfileImage src={speaker.image} alt={`${speaker.name}의 사진`}/>
      <ColContainer style={{ alignItems: 'flex-start' }}>
        <Text
          weight='800'
          size='16px'
          color='#FFFFFF'
          mb="2px"
          style={{ marginRight: '8px' }}
        >
          {speaker.name}
        </Text>
        <Text color='#CCCCCC' weight='400' size='14px' style={{
          textAlign: 'left',
          marginRight: 8,
        }}>
          { speaker.job }
        </Text>
      </ColContainer>

      <Email href={`mailto:${speaker.email}`}>
        <img src={EmailIcon} alt="Email Icon" width={24}/>
        <Text color='#FFFFFF' weight='600' size='16px' ml="6px">
          Mail
        </Text>
      </Email>
    </ProfileWrapper>
  ), [])

  const ExternalComponent = useMemo(() => {
    if (!session.externalLink) { return null }

    const {
      url,
      image,
      title,
      description,
    } = session.externalLink

    return (
    <ExternalWrapper>
      <External href={url} target="_blank">
        <ExternalImage src={image} alt="링크 이미지"/>
        <ColContainer style={{ alignItems: 'flex-start', marginLeft: 8 }}>
          <Text
            weight="800"
            size="16px"
            color="#EAEAEA"
            mb="2px"
          >
            { title }
          </Text>
          <Text
            weight="400"
            size="14px"
            color="#CCCCCC"
          >
            { description }
          </Text>
        </ColContainer>
      </External>
    </ExternalWrapper>
  )
  }, [session.externalLink])

  return session ? (
    <ColContainer w='100%' style={{ marginBottom: 100 }}>
      <Header onClick={goToHome}>
        <Logo w="44px" h="44px"/>
      </Header>
      { session.youtubeURL && (
        <MIframeContainer>
          <MIframe url={session.youtubeURL} />
        </MIframeContainer>
      )}
      <TextContainer style={{ alignItems: 'flex-start' }}>
        <SectionNameContainer bg={scColor}>
          <Text weight='900' size='18px' color='white' style={{ zIndex: 5 }}>
            {scName}
          </Text>
        </SectionNameContainer>
        <DText weight='800' size='28px' mt="16px">
          {session.title}
        </DText>
        <DText color="#EAEAEA" weight='400' size='16px' mt="16px" style={{ lineHeight: '150%' }}>
          {session.description}
        </DText>

        <SpeakersWrapper>
          { session.speakers.map(ProfileComponent)}
        </SpeakersWrapper>

        { ExternalComponent }

        <DLine />
        <ShareBtn
          bg={scColor}
          onClick={handleClickShare}
          data-clipboard-text={canUseShare ? undefined : clipboard}
        >
          <LinkImg />
          <ShareText>공유</ShareText>
        </ShareBtn>
      </TextContainer>
    </ColContainer>
  ) : (
    <div style={{ color: 'white' }}>로딩중...</div>
  );
}

export default Detail;
